import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Settings, Clock, Coffee, Briefcase, Save, Info, Umbrella, X, Monitor } from "lucide-react";
import { GET_SYSTEM_SETTINGS, UPDATE_SYSTEM_SETTINGS, GET_ALL_EMPLOYEES } from "../../graphql/queries";
import { toast } from "sonner";
import { Tooltip } from "../ui/tooltip";
import { StorageSettingsCard } from "./StorageSettingsCard";

interface SystemSettings {
  id: string;
  workDurationLimitMinutes: number;
  breakDurationLimitMinutes: number;
  smokeDurationLimitMinutes: number;
  maxSmokeBreakMinutes: number;
  leaveApprovalRequired: boolean;
  leaveApproverIds: string[];
  deviceLogRetentionDays: number;
  createdAt: string;
  updatedAt: string;
}

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
}

export const SystemSettingsManagement: React.FC = () => {
  const { data, loading, error, refetch } = useQuery<{ systemSettings: SystemSettings }>(
    GET_SYSTEM_SETTINGS,
    {
      fetchPolicy: "network-only",
    }
  );

  const [updateSettings, { loading: updating }] = useMutation(UPDATE_SYSTEM_SETTINGS, {
    onCompleted: () => {
      toast.success("Ustawienia zostały zaktualizowane");
      refetch();
    },
    onError: (error) => {
      toast.error(`Błąd: ${error.message}`);
    },
  });

  const { data: employeesData } = useQuery(GET_ALL_EMPLOYEES);

  const settings = data?.systemSettings;

  const [workLimit, setWorkLimit] = useState<number>(settings?.workDurationLimitMinutes || 480);
  const [breakLimit, setBreakLimit] = useState<number>(settings?.breakDurationLimitMinutes || 20);
  const [smokeLimit, setSmokeLimit] = useState<number>(settings?.smokeDurationLimitMinutes || 10);
  const [maxSmokeBreak, setMaxSmokeBreak] = useState<number>(settings?.maxSmokeBreakMinutes ?? 30);
  const [leaveApprovalRequired, setLeaveApprovalRequired] = useState<boolean>(settings?.leaveApprovalRequired ?? true);
  const [leaveApproverIds, setLeaveApproverIds] = useState<string[]>(settings?.leaveApproverIds || []);
  const [deviceLogRetentionDays, setDeviceLogRetentionDays] = useState<number>(settings?.deviceLogRetentionDays ?? 3);
  const [approverDropdownOpen, setApproverDropdownOpen] = useState(false);

  // Filter employees to ADMIN/MANAGER roles
  const approverCandidates: Employee[] = (employeesData?.employees || [])
    .filter((e: Employee) => (e.role === 'ADMIN' || e.role === 'MANAGER') && e.isActive)
    .sort((a: Employee, b: Employee) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`));

  // Update local state when settings load
  React.useEffect(() => {
    if (settings) {
      setWorkLimit(settings.workDurationLimitMinutes);
      setBreakLimit(settings.breakDurationLimitMinutes);
      setSmokeLimit(settings.smokeDurationLimitMinutes);
      setMaxSmokeBreak(settings.maxSmokeBreakMinutes ?? 30);
      setLeaveApprovalRequired(settings.leaveApprovalRequired);
      setLeaveApproverIds(settings.leaveApproverIds || []);
      setDeviceLogRetentionDays(settings.deviceLogRetentionDays ?? 3);
    }
  }, [settings]);

  const formatMinutesToHours = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}min`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${mins}min`;
    }
  };

  const handleSave = () => {
    updateSettings({
      variables: {
        data: {
          workDurationLimitMinutes: workLimit,
          breakDurationLimitMinutes: breakLimit,
          smokeDurationLimitMinutes: smokeLimit,
          maxSmokeBreakMinutes: maxSmokeBreak,
          leaveApprovalRequired,
          leaveApproverIds,
          deviceLogRetentionDays,
        },
      },
    });
  };

  const hasChanges = settings && (
    workLimit !== settings.workDurationLimitMinutes ||
    breakLimit !== settings.breakDurationLimitMinutes ||
    smokeLimit !== settings.smokeDurationLimitMinutes ||
    maxSmokeBreak !== (settings.maxSmokeBreakMinutes ?? 30) ||
    leaveApprovalRequired !== settings.leaveApprovalRequired ||
    JSON.stringify(leaveApproverIds) !== JSON.stringify(settings.leaveApproverIds || []) ||
    deviceLogRetentionDays !== settings.deviceLogRetentionDays
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">Ładowanie ustawień...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-2">Błąd podczas ładowania ustawień</p>
          <p className="text-sm text-muted-foreground">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-foreground flex items-center gap-2">
          <Settings className="w-8 h-8" />
          Konfiguracja systemu
        </h2>
        <p className="text-muted-foreground mt-2">
          Zarządzaj globalnymi ustawieniami limitów czasu dla wydarzeń
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Limity czasu trwania wydarzeń</CardTitle>
          <CardDescription>
            Wydarzenia przekraczające te limity będą wyróżniane kolorem czerwonym
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Work Duration Limit */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-primary" />
              <Label htmlFor="workLimit" className="text-base font-semibold">
                Limit czasu pracy
              </Label>
              <Tooltip
                content={
                  <div className="max-w-xs">
                    Maksymalny czas pojedynczej sesji pracy. <br />
                    Wydarzenia WORK_END przekraczające ten limit <br />
                    będą wyświetlane na czerwono.
                  </div>
                }
                side="top"
              >
                <Info className="w-4 h-4 text-muted-foreground cursor-help" />
              </Tooltip>
            </div>
            <div className="flex items-center gap-4">
              <Input
                id="workLimit"
                type="number"
                min="0"
                value={workLimit}
                onChange={(e) => setWorkLimit(parseInt(e.target.value) || 0)}
                className="max-w-xs"
              />
              <span className="text-sm text-muted-foreground">
                minut ({formatMinutesToHours(workLimit)})
              </span>
            </div>
          </div>

          {/* Break Duration Limit */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              <Label htmlFor="breakLimit" className="text-base font-semibold">
                Limit czasu przerwy
              </Label>
              <Tooltip
                content={
                  <div className="max-w-xs">
                    Maksymalny czas przerwy obiadowej. <br />
                    Wydarzenia BREAK_END przekraczające ten limit <br />
                    będą wyświetlane na czerwono.
                  </div>
                }
                side="top"
              >
                <Info className="w-4 h-4 text-muted-foreground cursor-help" />
              </Tooltip>
            </div>
            <div className="flex items-center gap-4">
              <Input
                id="breakLimit"
                type="number"
                min="0"
                value={breakLimit}
                onChange={(e) => setBreakLimit(parseInt(e.target.value) || 0)}
                className="max-w-xs"
              />
              <span className="text-sm text-muted-foreground">
                minut ({formatMinutesToHours(breakLimit)})
              </span>
            </div>
          </div>

          {/* Smoke Duration Limit */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Coffee className="w-5 h-5 text-primary" />
              <Label htmlFor="smokeLimit" className="text-base font-semibold">
                Limit czasu przerwy pozostałe
              </Label>
              <Tooltip
                content={
                  <div className="max-w-xs">
                    Maksymalny czas przerwy na papierosa/kawę. <br />
                    Wydarzenia SMOKE_END przekraczające ten limit <br />
                    będą wyświetlane na czerwono.
                  </div>
                }
                side="top"
              >
                <Info className="w-4 h-4 text-muted-foreground cursor-help" />
              </Tooltip>
            </div>
            <div className="flex items-center gap-4">
              <Input
                id="smokeLimit"
                type="number"
                min="0"
                value={smokeLimit}
                onChange={(e) => setSmokeLimit(parseInt(e.target.value) || 0)}
                className="max-w-xs"
              />
              <span className="text-sm text-muted-foreground">
                minut ({formatMinutesToHours(smokeLimit)})
              </span>
            </div>
          </div>

        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Maksymalna przerwa na papierosa (raport)
          </CardTitle>
          <CardDescription>
            Wpływa na podsumowanie czasu palenia w raporcie czasu pracy
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="maxSmokeBreak" className="text-base font-semibold">
              Próg uznania przerwy za błędną
            </Label>
            <Tooltip
              content={
                <div className="max-w-xs">
                  Przerwa na papierosa dłuższa niż ten próg nie jest <br />
                  wliczana do sumy czasu palenia w raporcie, tylko <br />
                  oznaczana jako anomalia do sprawdzenia. <br />
                  Chroni raport przed zawyżeniem, gdy ktoś zapomni <br />
                  odbić koniec przerwy (przerwa zostaje wtedy <br />
                  automatycznie zamknięta na kolejnym odbiciu).
                </div>
              }
              side="top"
            >
              <Info className="w-4 h-4 text-muted-foreground cursor-help" />
            </Tooltip>
          </div>
          <div className="flex items-center gap-4">
            <Input
              id="maxSmokeBreak"
              type="number"
              min="1"
              value={maxSmokeBreak}
              onChange={(e) => setMaxSmokeBreak(parseInt(e.target.value) || 0)}
              className="max-w-xs"
            />
            <span className="text-sm text-muted-foreground">
              minut ({formatMinutesToHours(maxSmokeBreak)})
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Umbrella className="w-5 h-5" />
            Zatwierdzanie nieobecności
          </CardTitle>
          <CardDescription>
            Ustawienia procesu zatwierdzania wniosków o nieobecność
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="leaveApprovalRequired"
              checked={leaveApprovalRequired}
              onChange={(e) => setLeaveApprovalRequired(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300"
            />
            <Label htmlFor="leaveApprovalRequired" className="text-base font-semibold cursor-pointer">
              Nowe nieobecności wymagają zatwierdzenia
            </Label>
            <Tooltip
              content={
                <div className="max-w-xs">
                  Gdy włączone, nieobecności składane przez pracowników <br />
                  będą miały status "Oczekujący" do momentu zatwierdzenia. <br />
                  Nieobecności dodawane przez administratora/managera <br />
                  są zatwierdzane automatycznie.
                </div>
              }
              side="top"
            >
              <Info className="w-4 h-4 text-muted-foreground cursor-help" />
            </Tooltip>
          </div>

          {leaveApprovalRequired && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-base font-semibold">
                  Osoby uprawnione do zatwierdzania nieobecności
                </Label>
                <Tooltip
                  content={
                    <div className="max-w-xs">
                      Wybierz osoby, które mogą zatwierdzać i odrzucać <br />
                      wnioski o nieobecność. Jeśli nie wybierzesz nikogo, <br />
                      każdy administrator i manager może zatwierdzać.
                    </div>
                  }
                  side="top"
                >
                  <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                </Tooltip>
              </div>

              <div className="relative">
                <div
                  className="min-h-[42px] border rounded-md p-2 cursor-pointer flex flex-wrap gap-1.5 items-center"
                  onClick={() => setApproverDropdownOpen(!approverDropdownOpen)}
                >
                  {leaveApproverIds.length === 0 ? (
                    <span className="text-sm text-muted-foreground">Wszyscy administratorzy i managerowie (domyślnie)</span>
                  ) : (
                    leaveApproverIds.map((id) => {
                      const emp = approverCandidates.find((e: Employee) => e.id === id);
                      return emp ? (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1 bg-primary/10 text-primary text-sm px-2 py-0.5 rounded"
                        >
                          {emp.lastName} {emp.firstName}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setLeaveApproverIds(leaveApproverIds.filter((aid) => aid !== id));
                            }}
                            className="hover:text-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ) : null;
                    })
                  )}
                </div>

                {approverDropdownOpen && (
                  <div className="absolute z-50 mt-1 w-full bg-background border rounded-md shadow-lg max-h-48 overflow-auto">
                    {approverCandidates
                      .filter((e: Employee) => !leaveApproverIds.includes(e.id))
                      .map((emp: Employee) => (
                        <div
                          key={emp.id}
                          className="px-3 py-2 hover:bg-muted cursor-pointer text-sm"
                          onClick={() => {
                            setLeaveApproverIds([...leaveApproverIds, emp.id]);
                            setApproverDropdownOpen(false);
                          }}
                        >
                          {emp.lastName} {emp.firstName}
                          <span className="ml-2 text-xs text-muted-foreground">
                            ({emp.role === 'ADMIN' ? 'Administrator' : 'Manager'})
                          </span>
                        </div>
                      ))}
                    {approverCandidates.filter((e: Employee) => !leaveApproverIds.includes(e.id)).length === 0 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground">Wszyscy zostali już dodani</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Monitor className="w-5 h-5" />
            Logi urządzeń
          </CardTitle>
          <CardDescription>
            Automatyczne czyszczenie logów weryfikacji urządzeń
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="deviceLogRetention" className="text-base font-semibold">
              Przechowywanie logów
            </Label>
            <Tooltip
              content={
                <div className="max-w-xs">
                  Wszystkie logi urządzeń (weryfikacje, błędy, akcje admina) <br />
                  są automatycznie usuwane po podanej liczbie dni. <br />
                  Ustaw 0, aby przechowywać logi na stałe.
                </div>
              }
              side="top"
            >
              <Info className="w-4 h-4 text-muted-foreground cursor-help" />
            </Tooltip>
          </div>
          <div className="flex items-center gap-4">
            <Input
              id="deviceLogRetention"
              type="number"
              min="0"
              max="365"
              value={deviceLogRetentionDays}
              onChange={(e) => setDeviceLogRetentionDays(parseInt(e.target.value) || 0)}
              className="max-w-xs"
            />
            <span className="text-sm text-muted-foreground">
              {deviceLogRetentionDays === 0
                ? "dni (przechowuj na stałe)"
                : deviceLogRetentionDays === 1
                  ? "dzień"
                  : `dni`}
            </span>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-4">
        <Button
          onClick={handleSave}
          disabled={!hasChanges || updating}
          className="flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {updating ? "Zapisywanie..." : "Zapisz zmiany"}
        </Button>
        {hasChanges && (
          <span className="text-sm text-amber-600">
            Masz niezapisane zmiany
          </span>
        )}
      </div>

      <StorageSettingsCard />

      {settings && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Informacje</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1 text-muted-foreground">
            <p>Ostatnia aktualizacja: {new Date(settings.updatedAt).toLocaleString("pl-PL")}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
